import { coutMensuelLigne, afficheQuantite } from '../../utils/coutsRecurrents';
import { formatMontant } from '../../utils/montants';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Bot,
  Send,
  Sparkles,
  ArrowLeft,
  Check,
  Save,
  RefreshCw,
  ChevronRight,
  User,
  Layers,
  Clock,
  AlertCircle,
  X,
  Paperclip,
  FileText,
  Image,
  File as FileIcon,
  DollarSign,
  Server,
  Cloud,
  Globe,
  Plug,
  Wrench,
  Shield,
  Calendar,
  FolderKanban,
  Copy
} from 'lucide-react';
import TemplateStarterDialog from '../../components/TemplateStarterDialog';

// Helper pour obtenir l'icône selon le type de fichier
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return <Image size={14} />;
  }
  if (['pdf', 'doc', 'docx'].includes(ext)) {
    return <FileText size={14} />;
  }
  return <FileIcon size={14} />;
};

// Composant Message
function ChatMessage({ message, isUser, files }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2
      }}
    >
      <Stack
        direction={isUser ? 'row-reverse' : 'row'}
        spacing={1.5}
        alignItems="flex-start"
        sx={{ maxWidth: '80%' }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: '50%',
            bgcolor: isUser ? 'rgba(99, 102, 241, 0.1)' : 'rgba(0, 212, 170, 0.1)',
            color: isUser ? '#6366f1' : '#00d4aa',
            flexShrink: 0
          }}
        >
          {isUser ? <User size={18} /> : <Bot size={18} />}
        </Box>
        <Paper
          sx={{
            p: 2,
            bgcolor: isUser ? 'rgba(99, 102, 241, 0.08)' : 'rgba(255,255,255,0.03)',
            border: isUser ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2,
            borderTopRightRadius: isUser ? 0 : 2,
            borderTopLeftRadius: isUser ? 2 : 0
          }}
        >
          {/* Affichage des fichiers joints */}
          {files && files.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {files.map((file, i) => (
                <Chip
                  key={i}
                  icon={getFileIcon(file)}
                  label={file}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: 'rgba(99, 102, 241, 0.05)',
                    borderColor: 'rgba(99, 102, 241, 0.3)'
                  }}
                />
              ))}
            </Stack>
          )}
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              '& strong': { fontWeight: 600 }
            }}
          >
            {message}
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
}

// Config des types de coûts récurrents
const recurringCostTypeConfig = {
  HOSTING: { label: 'Hébergement Web', icon: <Server size={14} />, color: '#3b82f6' },
  KUBERNETES: { label: 'Kubernetes / Cloud', icon: <Cloud size={14} />, color: '#8b5cf6' },
  EXTERNAL_API: { label: 'API Externe', icon: <Globe size={14} />, color: '#10b981' },
  EXTERNAL_SERVICE: { label: 'Service Externe', icon: <Plug size={14} />, color: '#f59e0b' },
  MAINTENANCE: { label: 'Maintenance', icon: <Wrench size={14} />, color: '#ec4899' },
  DOMAIN_SSL: { label: 'Domaine & SSL', icon: <Shield size={14} />, color: '#06b6d4' }
};

// Composant Preview du Template
function TemplatePreview({ templateState, onSave, saving }) {
  const hasContent = (templateState?.sections?.length > 0) || (templateState?.recurringCosts?.length > 0);
  
  if (!hasContent) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Layers size={48} strokeWidth={1} style={{ opacity: 0.3, marginBottom: 16 }} />
        <Typography variant="body2" color="text.secondary">
          Le template apparaîtra ici au fur et à mesure de la conversation
        </Typography>
      </Box>
    );
  }

  // Calculate totals - support both legacy and new dynamic hours format
  let totalHours = 0;
  templateState.sections?.forEach(section => {
    section.items?.forEach(item => {
      // Dynamic format: item.hours = { audit: 8, pentest: 16, ... }
      if (item.hours && typeof item.hours === 'object') {
        totalHours += Object.values(item.hours).reduce((sum, h) => sum + (h || 0), 0);
      }
    });
  });

  // Calculate monthly costs
  const totalMonthlyCost = templateState.recurringCosts?.reduce((sum, cost) => sum + coutMensuelLigne(cost), 0) || 0;

  return (
    <Stack spacing={2}>
      {/* Stats */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {templateState.sections?.length > 0 && (
          <Chip
            icon={<Layers size={14} />}
            label={`${templateState.sections.length} sections`}
            size="small"
            variant="outlined"
          />
        )}
        {totalHours > 0 && (
          <Chip
            icon={<Clock size={14} />}
            label={`${totalHours}h total`}
            size="small"
            variant="outlined"
            color="primary"
          />
        )}
        {totalMonthlyCost > 0 && (
          <Chip
            icon={<Calendar size={14} />}
            label={`${formatMontant(totalMonthlyCost)}$/mois`}
            size="small"
            variant="outlined"
            sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
          />
        )}
      </Stack>

      {/* Sections */}
      {templateState.sections.map((section, sIndex) => (
        <Card
          key={sIndex}
          sx={{
            bgcolor: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                label={section.type}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(0, 212, 170, 0.1)',
                  color: '#00d4aa'
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {section.name}
              </Typography>
            </Stack>
            
            {section.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {section.description}
              </Typography>
            )}

              {section.items && section.items.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {section.items.map((item, iIndex) => {
                    // Dynamic hours format
                    const hours = item.hours && typeof item.hours === 'object'
                      ? Object.values(item.hours).reduce((sum, h) => sum + (h || 0), 0)
                      : 0;
                    return (
                    <Stack
                      key={iIndex}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1,
                        bgcolor: 'rgba(255,255,255,0.02)',
                        borderRadius: 1,
                        fontSize: '0.8rem'
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ChevronRight size={14} style={{ opacity: 0.5 }} />
                        <Typography variant="body2">{item.name}</Typography>
                        {item.complexity && (
                          <Chip
                            label={item.complexity}
                            size="small"
                            sx={{ height: 16, fontSize: '0.6rem' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {hours}h
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Recurring Costs */}
      {templateState.recurringCosts && templateState.recurringCosts.length > 0 && (
        <>
          <Divider sx={{ my: 1 }}>
            <Chip 
              label="Coûts mensuels" 
              size="small" 
              icon={<DollarSign size={12} />}
              sx={{ fontSize: '0.7rem' }}
            />
          </Divider>
          
          <Card
            sx={{
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack spacing={1}>
                {templateState.recurringCosts.map((cost, cIndex) => {
                  const config = recurringCostTypeConfig[cost.type] || { 
                    label: cost.type, 
                    icon: <DollarSign size={14} />, 
                    color: '#888' 
                  };
                  return (
                    <Stack
                      key={cIndex}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1,
                        bgcolor: 'rgba(255,255,255,0.02)',
                        borderRadius: 1
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ color: config.color }}>{config.icon}</Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cost.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {config.label}
                          </Typography>
                        </Box>
                        {cost.isOptional && (
                          <Chip
                            label="Optionnel"
                            size="small"
                            sx={{ height: 16, fontSize: '0.55rem', opacity: 0.7 }}
                          />
                        )}
                      </Stack>
                      <Typography 
                        variant="body2" 
                        sx={{ fontWeight: 600, color: '#f59e0b' }}
                      >
                        {afficheQuantite(cost) ? `${cost.quantity} × ${formatMontant(cost.monthlyCost)}$ = ` : ''}{formatMontant(coutMensuelLigne(cost))}$/mois
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
              
              {/* Total mensuel */}
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total mensuel
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                  {formatMontant(totalMonthlyCost)}$/mois
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Save button - visible dès qu'il y a du contenu */}
      {hasContent && (
        <Button
          variant="contained"
          fullWidth
          onClick={onSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
          sx={{ mt: 2 }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer le template'}
        </Button>
      )}
    </Stack>
  );
}

// Composant Question de clarification
function ClarificationQuestion({ question, options, onAnswer }) {
  const [customAnswer, setCustomAnswer] = useState('');

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: 'rgba(245, 158, 11, 0.05)',
        border: '1px solid rgba(245, 158, 11, 0.2)',
        borderRadius: 2,
        mb: 2
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <AlertCircle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {question}
          </Typography>
          
          {options && options.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {options.map((opt, i) => (
                <Chip
                  key={i}
                  label={opt}
                  onClick={() => onAnswer(opt)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.1)' }
                  }}
                />
              ))}
            </Stack>
          )}
          
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              placeholder="Ou tapez votre réponse..."
              value={customAnswer}
              onChange={(e) => setCustomAnswer(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && customAnswer.trim()) {
                  onAnswer(customAnswer);
                  setCustomAnswer('');
                }
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (customAnswer.trim()) {
                  onAnswer(customAnswer);
                  setCustomAnswer('');
                }
              }}
              disabled={!customAnswer.trim()}
            >
              Répondre
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

// Types de fichiers acceptés
const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.md,.doc,.docx';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

// Composant principal
function AiTemplateBuilder() {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Project type selection
  const [step, setStep] = useState(0); // 0 = select type, 1 = chat
  const [projectTypes, setProjectTypes] = useState([]);
  const [selectedProjectTypeId, setSelectedProjectTypeId] = useState('');
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [templateState, setTemplateState] = useState({ sections: [], recurringCosts: [] });
  const [starterOpen, setStarterOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  
  // Dialog pour nommer le template
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [templateDescInput, setTemplateDescInput] = useState('');
  
  // Fetch project types on mount
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        const token = localStorage.getItem('portalToken');
        const response = await fetch('/api/project-types', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProjectTypes(data.filter(pt => pt.isActive));
        }
      } catch (err) {
        console.error('Fetch project types error:', err);
      }
    };
    fetchProjectTypes();
  }, []);
  
  // Check agent status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('portalToken');
        const response = await fetch('/api/ai/agents/template-builder/status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAgentStatus(data);
        }
      } catch (err) {
        setError('Erreur lors de la vérification de l\'agent');
      }
      setLoading(false);
    };
    
    checkStatus();
  }, []);
  
  // Start chat when project type is selected
  const handleStartChat = () => {
    const projectType = projectTypes.find(pt => pt.id === selectedProjectTypeId);
    setSelectedProjectType(projectType);
    setStep(1);
    
    // Add welcome message with project type context
    setMessages([{
      role: 'assistant',
      content: `Bonjour ! Je suis l'assistant de création de templates pour **${projectType?.name}**. 🎯\n\nDécrivez-moi le projet pour lequel vous souhaitez créer un template de devis, et je vous poserai quelques questions pour bien comprendre vos besoins.\n\nJe suis configuré avec les catégories, types d'heures et coûts récurrents spécifiques à ce type de projet.`
    }]);
  };
  
  // Reprend un template existant comme base de travail. Rien n'est modifie en
  // base : l'utilisateur enregistrera une nouvelle version a la fin.
  const handleStartFromTemplate = (loaded) => {
    const projectType = projectTypes.find(pt => pt.id === (loaded.projectTypeId || selectedProjectTypeId));
    setSelectedProjectType(projectType);
    if (loaded.projectTypeId) setSelectedProjectTypeId(loaded.projectTypeId);
    setTemplateState(loaded.state);
    setStarterOpen(false);
    setStep(1);

    const sectionCount = loaded.state.sections?.length || 0;
    const itemCount = (loaded.state.sections || []).reduce((sum, s) => sum + (s.items?.length || 0), 0);

    setMessages([{
      role: 'assistant',
      content: `J'ai chargé **${loaded.sourceName}** comme base : ${sectionCount} section(s) et ${itemCount} item(s).\n\n`
        + `Dites-moi ce que vous voulez changer — je peux modifier ou supprimer des sections, des items et des coûts récurrents, `
        + `ou en ajouter de nouveaux.\n\nL'original n'est pas touché : vous enregistrerez une nouvelle version à la fin.`
    }]);
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // File handling
  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    
    for (const file of fileArray) {
      // Check file count
      if (selectedFiles.length + validFiles.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} fichiers autorisés`);
        break;
      }
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`Le fichier "${file.name}" dépasse la limite de 10MB`);
        continue;
      }
      
      // Check if already selected
      if (selectedFiles.some(f => f.name === file.name)) {
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
    // Reset input
    e.target.value = '';
  };
  
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Drag & drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };
  
  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && selectedFiles.length === 0) || sending) return;
    
    const userMessage = inputMessage.trim();
    const filesToSend = [...selectedFiles];
    const fileNames = filesToSend.map(f => f.name);
    
    setInputMessage('');
    setSelectedFiles([]);
    setSending(true);
    setError('');
    
    // Add user message to UI
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage || (fileNames.length > 0 ? `[${fileNames.length} fichier(s) joint(s)]` : ''),
      files: fileNames.length > 0 ? fileNames : undefined
    }]);
    
    try {
      const token = localStorage.getItem('portalToken');
      
      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('message', userMessage || 'Analyse ces fichiers.');
      formData.append('conversationHistory', JSON.stringify(conversationHistory));
      formData.append('templateState', JSON.stringify(templateState));
      if (selectedProjectTypeId) {
        formData.append('projectTypeId', selectedProjectTypeId);
      }
      
      for (const file of filesToSend) {
        formData.append('files', file);
      }
      
      const response = await fetch('/api/ai/template/chat', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Add assistant response
        if (data.response) {
          setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        }
        
        // Update state
        setConversationHistory(data.conversationHistory || []);
        setTemplateState(data.templateState || { sections: [] });
      } else {
        const err = await response.json();
        setError(err.error || 'Erreur lors de la communication avec l\'agent');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
    
    setSending(false);
  };
  
  // Ouvrir le dialog pour nommer le template
  const openSaveDialog = () => {
    // Pré-remplir avec le nom suggéré par l'agent si disponible
    setTemplateNameInput(templateState.templateName || '');
    setTemplateDescInput(templateState.templateDescription || '');
    setSaveDialogOpen(true);
  };
  
  const handleSaveTemplate = async () => {
    if (!templateNameInput.trim()) {
      setError('Veuillez entrer un nom pour le template');
      return;
    }
    
    setSaveDialogOpen(false);
    setSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/ai/template/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          templateState,
          name: templateNameInput.trim(),
          description: templateDescInput.trim(),
          projectTypeId: selectedProjectTypeId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess('Template créé avec succès !');
        setTimeout(() => {
          navigate(`/admin/templates/${data.template.id}`);
        }, 1500);
      } else {
        const err = await response.json();
        setError(err.error || 'Erreur lors de la création du template');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
    
    setSaving(false);
  };
  
  const handleReset = () => {
    setStep(0);
    setSelectedProjectTypeId('');
    setSelectedProjectType(null);
    setMessages([]);
    setConversationHistory([]);
    setTemplateState({ sections: [], recurringCosts: [] });
    setError('');
    setSuccess('');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!agentStatus?.ready) {
    return (
      <Box>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/admin/templates')}
          sx={{ mb: 3 }}
        >
          Retour aux templates
        </Button>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          {!agentStatus?.llmConfigured
            ? 'Le LLM n\'est pas configuré. Veuillez configurer la clé API et sélectionner un modèle dans les paramètres.'
            : 'L\'agent template-builder n\'est pas actif.'}
        </Alert>
        
        <Button
          variant="contained"
          onClick={() => navigate('/admin/llm')}
        >
          Configurer le LLM
        </Button>
      </Box>
    );
  }
  
  // Step 0: Project type selection
  if (step === 0) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', py: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/admin/templates')}
            color="inherit"
          >
            Retour
          </Button>
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ p: 1, bgcolor: 'rgba(0, 212, 170, 0.1)', borderRadius: 1.5, color: '#00d4aa' }}>
              <Sparkles size={20} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Générateur de Templates AI
            </Typography>
          </Stack>
        </Stack>

        <Paper sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            Sélectionnez le type de projet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            L'assistant AI sera configuré avec les catégories, types d'heures et coûts récurrents spécifiques à ce type de projet.
          </Typography>

          <Stack spacing={2}>
            {projectTypes.map(pt => (
              <Paper
                key={pt.id}
                onClick={() => setSelectedProjectTypeId(pt.id)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  bgcolor: selectedProjectTypeId === pt.id ? `${pt.color}15` : 'rgba(255,255,255,0.01)',
                  border: selectedProjectTypeId === pt.id ? `2px solid ${pt.color}` : '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: `${pt.color}10`,
                    borderColor: `${pt.color}50`
                  }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: `${pt.color}15`, 
                    color: pt.color,
                    display: 'flex'
                  }}>
                    <FolderKanban size={24} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {pt.name}
                    </Typography>
                    {pt.description && (
                      <Typography variant="body2" color="text.secondary">
                        {pt.description}
                      </Typography>
                    )}
                  </Box>
                  {selectedProjectTypeId === pt.id && (
                    <Box sx={{ color: pt.color }}>
                      <Check size={24} />
                    </Box>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<ChevronRight size={20} />}
            onClick={handleStartChat}
            disabled={!selectedProjectTypeId}
            sx={{ mt: 3 }}
          >
            Commencer la conversation
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<Copy size={18} />}
            onClick={() => setStarterOpen(true)}
            disabled={!selectedProjectTypeId}
            sx={{ mt: 1.5 }}
          >
            Partir d'un template existant
          </Button>
        </Paper>

        <TemplateStarterDialog
          open={starterOpen}
          onClose={() => setStarterOpen(false)}
          projectTypeId={selectedProjectTypeId}
          onSelect={handleStartFromTemplate}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={() => setStep(0)}
            color="inherit"
          >
            Retour
          </Button>
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ p: 1, bgcolor: selectedProjectType?.color ? `${selectedProjectType.color}15` : 'rgba(0, 212, 170, 0.1)', borderRadius: 1.5, color: selectedProjectType?.color || '#00d4aa' }}>
              <Sparkles size={20} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Générateur de Templates AI
            </Typography>
            {selectedProjectType && (
              <Chip
                icon={<FolderKanban size={14} />}
                label={selectedProjectType.name}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: `${selectedProjectType.color}15`,
                  color: selectedProjectType.color,
                  '& .MuiChip-icon': { color: selectedProjectType.color }
                }}
              />
            )}
          </Stack>
        </Stack>
        
        <Button
          startIcon={<RefreshCw size={16} />}
          onClick={handleReset}
          color="inherit"
          variant="outlined"
        >
          Réinitialiser
        </Button>
      </Stack>
      
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" icon={<Check size={20} />} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Main content */}
      <Box sx={{ display: 'flex', gap: 3, flex: 1, minHeight: 0 }}>
        {/* Chat area */}
        <Paper
          sx={{
            flex: 2,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'rgba(255,255,255,0.01)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {messages.map((msg, i) => (
              <ChatMessage 
                key={i} 
                message={msg.content} 
                isUser={msg.role === 'user'} 
                files={msg.files}
              />
            ))}
            
            {/* Pending question */}
            {templateState.pendingQuestion && (
              <ClarificationQuestion
                question={templateState.pendingQuestion.question}
                options={templateState.pendingQuestion.options}
                onAnswer={(answer) => {
                  setInputMessage(answer);
                  setTimeout(() => handleSendMessage(), 100);
                }}
              />
            )}
            
            {/* Loading indicator */}
            {sending && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 5, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  L'agent réfléchit...
                </Typography>
              </Box>
            )}
            
            <div ref={chatEndRef} />
          </Box>
          
          {/* Input */}
          <Box 
            sx={{ 
              p: 2, 
              borderTop: '1px solid rgba(255,255,255,0.06)',
              position: 'relative',
              bgcolor: dragActive ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
              transition: 'background-color 0.2s'
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {/* Drop overlay */}
            {dragActive && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                  border: '2px dashed rgba(99, 102, 241, 0.5)',
                  borderRadius: 2,
                  zIndex: 10,
                  pointerEvents: 'none'
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <Paperclip size={32} color="#6366f1" />
                  <Typography variant="body2" color="primary">
                    Déposez vos fichiers ici
                  </Typography>
                </Stack>
              </Box>
            )}
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            
            {/* Selected files preview */}
            {selectedFiles.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {selectedFiles.map((file, i) => (
                  <Chip
                    key={i}
                    icon={getFileIcon(file.name)}
                    label={file.name.length > 20 ? file.name.slice(0, 17) + '...' : file.name}
                    size="small"
                    onDelete={() => removeFile(i)}
                    deleteIcon={<X size={14} />}
                    sx={{
                      height: 28,
                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                      borderColor: 'rgba(99, 102, 241, 0.3)',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255,255,255,0.5)',
                        '&:hover': { color: '#ef4444' }
                      }
                    }}
                  />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {selectedFiles.length}/{MAX_FILES} fichiers
                </Typography>
              </Stack>
            )}
            
            <Stack direction="row" spacing={1}>
              {/* Attachment button */}
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || selectedFiles.length >= MAX_FILES}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  '&:hover': {
                    bgcolor: 'rgba(99, 102, 241, 0.1)',
                    borderColor: 'rgba(99, 102, 241, 0.3)'
                  }
                }}
                title="Joindre des fichiers (images, PDF, documents)"
              >
                <Paperclip size={20} />
              </IconButton>
              
              <TextField
                fullWidth
                placeholder="Décrivez votre projet ou répondez à l'agent..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={sending}
                multiline
                maxRows={4}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={(!inputMessage.trim() && selectedFiles.length === 0) || sending}
                        color="primary"
                      >
                        <Send size={20} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.02)'
                  }
                }}
              />
            </Stack>
            
            {/* Help text */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Glissez-déposez ou cliquez sur 📎 pour joindre des fichiers (images, PDF, TXT, MD, DOC)
            </Typography>
          </Box>
        </Paper>
        
        {/* Preview area */}
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'rgba(255,255,255,0.01)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 320
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Layers size={18} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Aperçu du Template
              </Typography>
            </Stack>
          </Box>
          
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <TemplatePreview
              templateState={templateState}
              onSave={openSaveDialog}
              saving={saving}
            />
          </Box>
        </Paper>
      </Box>
      
      {/* Dialog pour nommer le template */}
      <Dialog 
        open={saveDialogOpen} 
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Nommer le template
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Nom du template"
              fullWidth
              value={templateNameInput}
              onChange={(e) => setTemplateNameInput(e.target.value)}
              placeholder="Ex: Site vitrine notaire, E-commerce PME..."
              required
            />
            <TextField
              label="Description (optionnel)"
              fullWidth
              multiline
              rows={2}
              value={templateDescInput}
              onChange={(e) => setTemplateDescInput(e.target.value)}
              placeholder="Description du template..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained"
            disabled={!templateNameInput.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AiTemplateBuilder;
